import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Bell, Sun, Moon, LogOut, Dumbbell, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getUnreadCount, getActiveBroadcasts } from '../../api/notificationApi';
import { getMyGym } from '../../api/gymApi';

const PAGE_META = {
  '/': { title: 'Dashboard', subtitle: 'Overview of your gym' },
  '/my-gym': { title: 'My Gym', subtitle: 'Manage your gym listing' },
  '/pricing-slots': { title: 'Slots', subtitle: 'Bookable time slots calendar' },
  '/listing-preview': { title: 'Listing Preview', subtitle: 'How your gym appears to members' },
  '/bookings': { title: 'Bookings', subtitle: 'Customer bookings & revenue' },
  '/analytics': { title: 'Analytics', subtitle: 'Traffic, retention & revenue trends' },
  '/reviews': { title: 'Reviews', subtitle: 'Ratings & member feedback' },
  '/notifications': { title: 'Notifications', subtitle: 'Broadcasts and account alerts' },
  '/payments': { title: 'Payments & Subscription', subtitle: 'Billing plan & payout status' },
  '/profile': { title: 'Profile', subtitle: 'Account & security settings' },
  '/support': { title: 'Support', subtitle: 'Get help or track your tickets' },
};

export default function Header({ onMenuClick, isSidebarCollapsed, onToggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const meta = PAGE_META[location.pathname] || { title: 'GymPortal', subtitle: '' };

  const [unreadCount, setUnreadCount] = useState(0);
  const [gymName, setGymName] = useState('');

  useEffect(() => {
    getMyGym().then((gym) => setGymName(gym?.name || '')).catch(() => {});
  }, []);

  useEffect(() => {
    const refresh = () => {
      Promise.all([getUnreadCount(), getActiveBroadcasts()])
        .then(([count, broadcasts]) => setUnreadCount(count + (broadcasts?.length || 0)))
        .catch(() => {});
    };

    refresh();
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initial = (gymName || 'G').charAt(0).toUpperCase();

  return (
    <header
      className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 sm:px-6 transition-all bg-white/[0.94] dark:bg-[#1b1b1b]/[0.94] border-b border-[rgba(60,64,67,0.08)] dark:border-[#3c4043] shadow-[0_2px_12px_rgba(60,64,67,0.06)]"
      style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }}
    >
      <div className="flex items-center gap-3 min-w-0 dark:text-[#e8eaed]">
        <button onClick={onMenuClick} className="lg:hidden p-2 rounded-xl text-[#5f6368] hover:bg-[#f1f3f4] cursor-pointer shrink-0">
          <Menu className="w-5 h-5" />
        </button>
        <button
          onClick={onToggleSidebar}
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden lg:flex p-2 rounded-xl text-[#5f6368] hover:bg-[#f1f3f4] dark:text-[#9aa0a6] dark:hover:bg-[#303134] cursor-pointer shrink-0"
        >
          {isSidebarCollapsed ? <PanelLeftOpen className="w-[18px] h-[18px]" /> : <PanelLeftClose className="w-[18px] h-[18px]" />}
        </button>
        <div className="hidden sm:flex w-9 h-9 rounded-xl items-center justify-center shrink-0 bg-[#e8f0fe] text-[#1a73e8]">
          <Dumbbell className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-base sm:text-lg font-semibold text-[#202124] dark:text-[#e8eaed] truncate">{meta.title}</h1>
          {meta.subtitle && <p className="text-[11px] text-[#5f6368] dark:text-[#9aa0a6] truncate hidden sm:block">{meta.subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="p-2 rounded-full text-[#5f6368] hover:bg-[#f1f3f4] dark:text-[#9aa0a6] dark:hover:bg-[#303134] cursor-pointer"
        >
          {isDark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
        </button>

        <button
          onClick={() => navigate('/notifications')}
          title="Notifications"
          className="relative p-2 rounded-full text-[#5f6368] hover:bg-[#f1f3f4] dark:text-[#9aa0a6] dark:hover:bg-[#303134] cursor-pointer"
        >
          <Bell className="w-[18px] h-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#EA4335] ring-2 ring-white dark:ring-[#1b1b1b]" />
          )}
        </button>

        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#1a73e8] to-[#8ab4f8] text-white flex items-center justify-center text-xs font-bold shrink-0">
          {initial}
        </div>

        <button
          onClick={handleLogout}
          className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] hover:text-[#EA4335] cursor-pointer px-2"
        >
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </div>
    </header>
  );
}
